import { ActionTree, GetterTree, MutationTree } from "vuex";
import { AssistClient } from "@/lib/clients/assistClient";
import { Assist, AssistFunctionType } from "@/lib/models/assist";

// 型定義
export interface HistoryItem {
  id: string;
  feedbackPreview: string;
  createdAt: Date;
  fullFeedback: string;
  requestTitle?: string;
  writerType?: string;
  functionType: AssistFunctionType;
  completed: boolean;
  isError: boolean;
}

// State型定義
export interface AssistHistoryState {
  historyList: HistoryItem[];
  loading: boolean;
  error: string | null;
  // 🆕 ポーリング状態追加
  polling: {
    isActive: boolean;
    intervalId: number | null;
  };
}

// Mutations型定義
interface Mutations {
  setHistoryList(state: AssistHistoryState, historyList: HistoryItem[]): void;
  clearHistoryList(state: AssistHistoryState): void;
  setLoading(state: AssistHistoryState, loading: boolean): void;
  setError(state: AssistHistoryState, error: string | null): void;
  clearError(state: AssistHistoryState): void;
  // 🆕 ポーリング用 mutations 追加
  setPollingActive(state: AssistHistoryState, isActive: boolean): void;
  setPollingIntervalId(state: AssistHistoryState, intervalId: number | null): void;
  updateHistoryItem(state: AssistHistoryState, updatedItem: HistoryItem): void;
}

// Actions型定義
interface Actions {
  fetchHistoryList(context: any): Promise<void>;
  refreshHistoryList(context: any): Promise<void>;
  deleteHistoryItem(context: any, historyId: string): Promise<void>;
  getHistoryDetail(context: any, assistId: string): Promise<HistoryItem | null>;
  // 🆕 ポーリング用 actions 追加
  startPolling(context: any): Promise<void>;
  stopPolling(context: any): Promise<void>;
  pollIncompleteItems(context: any): Promise<void>;
  // 🆕 状態リセット用 action 追加
  resetState(context: any): Promise<void>;
}

// Getters型定義
interface Getters {
  isLoading(state: AssistHistoryState): boolean;
  hasError(state: AssistHistoryState): boolean;
  errorMessage(state: AssistHistoryState): string | null;
  filteredHistoryList(state: AssistHistoryState): HistoryItem[];
  historyCount(state: AssistHistoryState, getters: any): number;
}

// データ変換関数（API仕様準拠）
const convertAssistLogToHistoryItem = (assistLog: Assist): HistoryItem => {
  // 機能種別に応じたテキスト取得
  let feedbackText = "";
  if (assistLog.functionType === AssistFunctionType.FEEDBACK_MANUSCRIPT) {
    // 感想生成: メッセージをそのまま使用
    feedbackText = assistLog.result?.message || "";
  } else if (assistLog.functionType === AssistFunctionType.MATCHING_COMPANY) {
    // 応募先マッチング: HTMLからテキストを抽出
    feedbackText = assistLog.result?.html || "";
  }

  const feedbackPreview = feedbackText.length > 100 ? `${feedbackText.substring(0, 100)}...` : feedbackText;

  // 状態に応じた表示テキスト決定
  let displayPreview: string;
  if (assistLog.completed && !assistLog.isError) {
    displayPreview = feedbackPreview;
  } else if (assistLog.isError) {
    displayPreview = "ごめんなさい。。失敗しました...";
  } else {
    displayPreview = "アシスタント猫が一生懸命考えています...";
  }

  return {
    id: assistLog.assistId,
    feedbackPreview: displayPreview,
    createdAt: new Date(assistLog.createdAt),
    fullFeedback: feedbackText,
    requestTitle: assistLog.requestTitle || undefined,
    writerType: assistLog.writerType || undefined,
    functionType: assistLog.functionType,
    completed: assistLog.completed,
    isError: assistLog.isError || false,
  };
};

// Factory Function: AssistHistoryModule作成
export const createAssistHistoryModule = (assistClient: AssistClient = new AssistClient()) => {
  // State
  const state: AssistHistoryState = {
    historyList: [],
    loading: false,
    error: null,
    // 🆕 ポーリング初期状態
    polling: {
      isActive: false,
      intervalId: null,
    },
  };

  // Mutations
  const mutations: MutationTree<AssistHistoryState> & Mutations = {
    setHistoryList(state, historyList) {
      state.historyList = historyList;
    },

    clearHistoryList(state) {
      state.historyList = [];
    },

    setLoading(state, loading) {
      state.loading = loading;
    },

    setError(state, error) {
      state.error = error;
    },

    clearError(state) {
      state.error = null;
    },

    // 🆕 ポーリング用 mutations 追加
    setPollingActive(state, isActive) {
      state.polling.isActive = isActive;
    },

    setPollingIntervalId(state, intervalId) {
      state.polling.intervalId = intervalId;
    },

    // 🆕 個別アイテム更新（ポーリング用）
    updateHistoryItem(state, updatedItem) {
      const index = state.historyList.findIndex((item) => item.id === updatedItem.id);
      if (index !== -1) {
        state.historyList.splice(index, 1, updatedItem);
      }
    },
  };

  // Actions（注入されたassistClientを使用）
  const actions: ActionTree<AssistHistoryState, {}> & Actions = {
    async fetchHistoryList({ commit, state, rootGetters }) {
      try {
        commit("setLoading", true);
        commit("clearError");

        // assistSelectManuscriptモジュールから現在選択中の作品IDを取得
        const currentNovelId = rootGetters["assistSelectManuscriptModule/selectedNovelId"];

        if (!currentNovelId) {
          commit("setError", "作品が選択されていません");
          return;
        }

        // 注入されたassistClientを使用してAPI呼び出し
        const assistLogs = await assistClient.listAssistLog({
          novelId: currentNovelId,
        });

        // データ変換
        const transformedData = assistLogs.map((assistLog) => convertAssistLogToHistoryItem(assistLog));

        commit("setHistoryList", transformedData);
      } catch (error: any) {
        // エラー分類とメッセージ設定
        let errorMessage = "履歴データの取得に失敗しました";

        if (error.message?.includes("認証")) {
          errorMessage = "認証エラーが発生しました。再度ログインしてください。";
        } else if (error.message?.includes("ネットワーク")) {
          errorMessage = "ネットワーク接続を確認してください。";
        } else if (error.response?.status === 403) {
          errorMessage = "この操作を実行する権限がありません。";
        }

        commit("setError", errorMessage);
      } finally {
        commit("setLoading", false);
      }
    },

    async refreshHistoryList({ commit, dispatch }) {
      commit("clearHistoryList");
      await dispatch("fetchHistoryList");
    },

    async deleteHistoryItem({ commit, state }, historyId) {
      try {
        commit("setLoading", true);
        commit("clearError");

        // TODO: 削除API実装（現在は仕様に含まれていないため、ローカル削除のみ）

        // ローカルリストから削除
        const updatedList = state.historyList.filter((item) => item.id !== historyId);
        commit("setHistoryList", updatedList);
      } catch (error: any) {
        commit("setError", "履歴の削除に失敗しました");
      } finally {
        commit("setLoading", false);
      }
    },

    async getHistoryDetail({ commit, rootGetters, state }, assistId: string): Promise<HistoryItem | null> {
      try {
        // assistSelectManuscriptモジュールから現在選択中の作品IDを取得
        const currentNovelId = rootGetters["assistSelectManuscriptModule/selectedNovelId"];

        if (!currentNovelId) {
          throw new Error("作品が選択されていません");
        }

        // 履歴リストから対象アイテムを検索してfunctionTypeを取得
        const targetItem = state.historyList.find((item) => item.id === assistId);
        if (!targetItem) {
          throw new Error("対象の履歴アイテムが見つかりません");
        }

        // 注入されたassistClientを使用して詳細データ取得
        const assistLog = await assistClient.getAssistLog({
          novelId: currentNovelId,
          assistId,
          functionType: targetItem.functionType,
        });

        return convertAssistLogToHistoryItem(assistLog);
      } catch (error: any) {
        commit("setError", "履歴詳細の取得に失敗しました");
        return null;
      }
    },

    // 🆕 ポーリング用 actions 追加
    async startPolling({ commit, dispatch, state }) {
      // 既にポーリング中なら停止
      if (state.polling.isActive) {
        await dispatch("stopPolling");
      }

      // 未完了アイテム確認
      const incompleteItems = state.historyList.filter((item) => !item.completed && !item.isError);

      if (incompleteItems.length === 0) {
        return;
      }

      commit("setPollingActive", true);

      const intervalId = setInterval(async () => {
        await dispatch("pollIncompleteItems");
      }, 10000); // 10秒間隔

      commit("setPollingIntervalId", intervalId);
    },

    async stopPolling({ commit, state }) {
      if (state.polling.intervalId) {
        clearInterval(state.polling.intervalId);
        commit("setPollingIntervalId", null);
      }
      commit("setPollingActive", false);
    },

    async pollIncompleteItems({ state, commit, dispatch, rootGetters }) {
      // 未完了アイテム確認
      const incompleteItems = state.historyList.filter((item) => !item.completed && !item.isError);

      if (incompleteItems.length === 0) {
        await dispatch("stopPolling");
        return;
      }

      try {
        // 🆕 効率的な個別アイテム更新
        const currentNovelId = rootGetters["assistSelectManuscriptModule/selectedNovelId"];

        if (!currentNovelId) {
          return;
        }

        // 未完了アイテムのみを個別にチェック
        const updatePromises = incompleteItems.map(async (item) => {
          try {
            const assistLog = await assistClient.getAssistLog({
              novelId: currentNovelId,
              assistId: item.id,
              functionType: item.functionType,
            });

            const updatedItem = convertAssistLogToHistoryItem(assistLog);

            // 状態が変更された場合のみ更新
            const hasChanged =
              item.completed !== updatedItem.completed ||
              item.isError !== updatedItem.isError ||
              item.feedbackPreview !== updatedItem.feedbackPreview;

            if (hasChanged) {
              commit("updateHistoryItem", updatedItem);
              return true; // 変更あり
            }

            return false; // 変更なし
          } catch (error) {
            return false;
          }
        });

        await Promise.all(updatePromises);
      } catch (error) {
        // エラーが発生してもポーリングは継続
      }
    },

    // �� 状態リセット用 action 追加
    async resetState({ commit }) {
      commit("clearHistoryList");
      commit("clearError");
      commit("setLoading", false);
      commit("setPollingActive", false);
      commit("setPollingIntervalId", null);
    },
  };

  // Getters
  const getters: GetterTree<AssistHistoryState, {}> & Getters = {
    isLoading: (state) => state.loading,

    hasError: (state) => state.error !== null,

    errorMessage: (state) => state.error,

    filteredHistoryList: (state) =>
      // 作成日時の降順でソート（安全なDate変換付き）
      [...state.historyList].sort((a, b) => {
        const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
        const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
        return dateB.getTime() - dateA.getTime();
      }),
    historyCount: (state, getters) => getters.filteredHistoryList.length,
  };

  // モジュール定義を返す
  return {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
  };
};

// エクスポート
export { convertAssistLogToHistoryItem };

// テスト用: モックAssistClientでモジュールを作成
export const createAssistHistoryModuleForTesting = (mockMethods?: Partial<AssistClient>) => {
  // モッククライアントの実装
  class MockAssistClient extends AssistClient {
    constructor() {
      super();
      // mockMethodsで提供されたメソッドで上書き
      if (mockMethods) {
        Object.assign(this, mockMethods);
      }
    }

    // デフォルトのモック実装
    async listAssistLog(input: any): Promise<Assist[]> {
      return []; // 空配列を返すデフォルト実装
    }

    async getAssistLog(input: any): Promise<Assist> {
      throw new Error("Mock implementation required for getAssistLog");
    }

    async createFeedbackManuscript(input: any): Promise<Assist> {
      throw new Error("Mock implementation required for createFeedbackManuscript");
    }
  }

  return createAssistHistoryModule(new MockAssistClient());
};

// 後方互換性のためのデフォルトエクスポート（AssistClientを新規作成）
export default createAssistHistoryModule();
